import { toSeconds } from '@art-suite/art-core-ts';

import {
  getAllScopedTasks,
  getAllTasksById,
  getLatestProgressStatus,
  isScanPartScoped,
  isTaskScoped,
  isTaskShownOnScopeReport,
  ScanGroupOptimized,
  ScanLineItem,
  scanPartHasPhotos,
  scanPartHasValues,
  ScanPartOptimized,
  ScanRootOptimized,
  ScanTaskOptimized,
  taskHasScopeChanges,
  WorkStatus,
} from '@kaiizen/kai-scan-template-system';

import { ApiCreateScanNote, ApiScanNote, ScanType } from '../ApiSchemas';

/**
 * Find the most recent note for a specific scanPartId that matches one of the specified note types
 * @param scanNotes - Array of all scan notes
 * @param scanPartId - The scanPartId to filter by
 * @param noteTypes - Array of note types to consider
 * @returns The most recent matching note, or undefined if none found
 */
export const getMostRecentRelevantNote = (
  scanNotes: ApiScanNote[],
  scanPartId: string,
  noteTypes: string[],
  scanId?: string,
): ApiScanNote | undefined => {
  let mostRecentRelevantNote: ApiScanNote | undefined;

  for (const note of scanNotes) {
    if (scanId && note.scanId !== scanId) continue;
    if (note.scanPartId === scanPartId && noteTypes.includes(note.scanNoteType)) {
      if (!mostRecentRelevantNote || toSeconds(note.createdAt) > toSeconds(mostRecentRelevantNote.createdAt)) {
        mostRecentRelevantNote = note;
      }
    }
  }

  return mostRecentRelevantNote;
};

const APPROVAL_RELATED_NOTE_TYPES = ['editRequest', 'editResolution', 'approval'];

/**
 * @param scanNotes
 * @param scanPartId
 * @returns
 */
export const getMostRecentApprovalRelatedNote = (
  scanNotes: ApiScanNote[],
  scanPartId: string,
): ApiScanNote | undefined => getMostRecentRelevantNote(scanNotes, scanPartId, APPROVAL_RELATED_NOTE_TYPES);

export type ScanPartApprovalStatus = 'approved' | 'editRequested' | 'editResolved' | 'needsReview';

/**
 * Context for checking approval status
 */
export type ApprovalContext = {
  scanId: string;
  scanNotesByScanPartId: Record<string, ApiScanNote[]>;
  baselineTasksById?: Record<string, ScanTaskOptimized>;
  defaultContractorsByTrade?: Record<string, string | null>;
  baselineDefaultContractorsByTrade?: Record<string, string | null>;
};

export const getScanPartApprovalStatus = (
  scanPart: ScanPartOptimized,
  approvalContext: ApprovalContext,
): ScanPartApprovalStatus => {
  if (isScanPartApproved(scanPart, approvalContext)) return 'approved';
  if (isScanPartEditRequested(scanPart, approvalContext)) return 'editRequested';
  if (isScanPartEditResolved(scanPart, approvalContext)) return 'editResolved';
  return 'needsReview';
};

export type TaskStatusPhase = 'scoping' | 'construction';

const CONSTRUCTION_SCAN_TYPES: ScanType[] = ['progress', 'progressRework', 'progressCompleted'];

/**
 * Which of the two status-chip phases a document is in, independent of any one
 * task's own state. A progress-type scan is Construction regardless of its own
 * lifecycle status — approving a scope/change-order scan spawns a brand-new
 * 'progress' child scan (see `source/Models/Scan.ts`'s `updateScan`) rather than
 * transitioning the same record forward. Everything else is Scoping — this ladder
 * no longer distinguishes "submitted for review" from "still being drafted";
 * see `getOverallTaskStatus`.
 */
export const getTaskStatusPhase = (
  { effectiveScanType }: {
    effectiveScanType?: ScanType;
  },
): TaskStatusPhase => {
  if (effectiveScanType && CONSTRUCTION_SCAN_TYPES.includes(effectiveScanType)) return 'construction';
  return 'scoping';
};

/**
 * Where a task sits in its whole life, from "someone just added it" through to
 * "the work is done" — one value answering the question every surface asks.
 *
 * The last four values are {@link WorkStatus} verbatim, so a task under
 * construction reports exactly what the progress walk recorded.
 */
export type OverallTaskStatus =
  | 'empty'
  | 'draft'
  | 'approved'
  | WorkStatus;

/**
 * Resolves a task's {@link OverallTaskStatus} for its current {@link TaskStatusPhase}.
 *
 * - **construction**: the work ladder, verbatim — `getLatestProgressStatus`, or
 *   `notStarted` once approved for work but before anyone has recorded anything.
 *   Cost/values/notes don't matter here; a task can be picked up fresh mid-construction
 *   and it still just reads `notStarted`, not `empty`.
 * - **scoping**: `empty` if the task carries no cost, values, photos, or notes;
 *   `approved` once individually approved; otherwise `draft`.
 */
export const getOverallTaskStatus = (
  task: ScanTaskOptimized,
  { phase, approvalStatus, taskHasNotes }: {
    phase: TaskStatusPhase;
    approvalStatus?: ScanPartApprovalStatus;
    taskHasNotes?: boolean;
  },
): OverallTaskStatus => {
  if (phase === 'construction') return getLatestProgressStatus(task) ?? 'notStarted';

  const taskIsEmpty = !isTaskScoped(task) && !scanPartHasValues(task) && !scanPartHasPhotos(task) && !taskHasNotes;
  if (taskIsEmpty) return 'empty';
  if (approvalStatus === 'approved') return 'approved';
  return 'draft';
};

/**
 * if the note-types 'editRequest', 'editResolution', or 'approval' are present,
 * this is true if the last one of those types was 'approval' for the current scan.
 *
 * If the scanPart needs approval, any edit after that approval makes it stale: the
 * approval must be newer than the scanPart's updatedAt. A scanPart needs approval if:
 * - initial scope: it is scoped (non-$0 total cost)
 * - change order: it has scope changes vs baseline
 *
 * For Change Orders: if a task hasn't changed vs baseline, it's automatically approved
 * @param scanPart
 * @param approvalContext - context containing scan notes and baseline info
 * @returns
 */
export const isScanPartApproved = (
  scanPart: ScanPartOptimized,
  approvalContext: ApprovalContext,
): boolean => {
  const { baselineTasksById } = approvalContext;
  const isChangeOrderScan = !!baselineTasksById && scanPart.scanPartType === 'task';
  const baselineTask = isChangeOrderScan ? baselineTasksById[scanPart.id] : undefined;
  const isChangeOrderTask = isChangeOrderScan && taskHasScopeChanges(
    scanPart as ScanTaskOptimized,
    baselineTask,
    approvalContext.defaultContractorsByTrade,
    approvalContext.baselineDefaultContractorsByTrade,
  );

  // For Change Orders: if task hasn't changed vs baseline, it's automatically approved
  if (baselineTask && !isChangeOrderTask) return true;

  // Otherwise check for explicit approval note for this scan
  const scanNotes = approvalContext.scanNotesByScanPartId[scanPart.id] || [];
  const mostRecentNote = getMostRecentRelevantNote(scanNotes, scanPart.id, ['editRequest', 'editResolution', 'approval']);
  if (mostRecentNote?.scanNoteType !== 'approval' || mostRecentNote.scanId !== approvalContext.scanId) return false;

  const needsApproval = isChangeOrderScan ? isChangeOrderTask : isScanPartScoped(scanPart);
  return !needsApproval || toSeconds(mostRecentNote.createdAt) > toSeconds(scanPart.updatedAt ?? 0);
};

export const createUnapprovalNote = (
  scanId: string,
  scanPartId: string,
  scanNotesByScanPartId: Record<string, ApiScanNote[]>,
): Omit<ApiCreateScanNote, 'userId'> | undefined => {
  const scanNotes = scanNotesByScanPartId[scanPartId] || [];
  const mostRecentNote = getMostRecentRelevantNote(scanNotes, scanPartId, ['editRequest', 'editResolution', 'approval'], scanId);
  if (mostRecentNote?.scanNoteType === 'approval') {
    return {
      scanPartId,
      scanNoteType: 'delete',
      scanId,
      createdAt: new Date(),
      parentScanNoteId: mostRecentNote.id,
      body: '',
    };
  }
};

/**
 * if the note-types 'editRequest', 'editResolution', or 'approval' are present,
 * this is true if the last one of those types was 'editRequest'
 * @param scanPart
 * @param approvalContext
 * @returns
 */
export const isScanPartEditRequested = (scanPart: ScanPartOptimized, approvalContext: ApprovalContext): boolean => {
  const scanNotes = approvalContext.scanNotesByScanPartId[scanPart.id] || [];
  const mostRecentNote = getMostRecentRelevantNote(scanNotes, scanPart.id, ['editRequest', 'editResolution', 'approval']);
  return mostRecentNote?.scanNoteType === 'editRequest';
};

/**
 * if the note-types 'editRequest', 'editResolution', or 'approval' are present,
 * this is true if the last one of those types was 'approval' or 'editRequest'
 *
 * For Change Orders: if a task hasn't changed vs baseline, it's automatically reviewed
 * @param scanPart
 * @param approvalContext - context containing scan notes and baseline info
 * @returns
 */
export const isScanPartReviewed = (
  scanPart: ScanPartOptimized,
  approvalContext: ApprovalContext,
): boolean => {
  // For Change Orders: if task hasn't changed vs baseline, it's automatically reviewed/approved
  if (approvalContext.baselineTasksById && scanPart.scanPartType === 'task') {
    const baselineTask = approvalContext.baselineTasksById[scanPart.id];
    if (baselineTask) {
      const hasChanges = taskHasScopeChanges(
        scanPart as ScanTaskOptimized,
        baselineTask,
        approvalContext.defaultContractorsByTrade,
        approvalContext.baselineDefaultContractorsByTrade,
      );
      if (!hasChanges) return true; // No changes vs baseline = automatically reviewed
    }
  }

  // Otherwise check for review note
  const scanNotes = approvalContext.scanNotesByScanPartId[scanPart.id] || [];
  const mostRecentNote = getMostRecentRelevantNote(scanNotes, scanPart.id, ['editRequest', 'editResolution', 'approval']);
  if (!mostRecentNote) return false;
  if (mostRecentNote.scanNoteType === 'editResolution') return false;

  const approvalIsForCurrentScan = mostRecentNote.scanId === approvalContext.scanId;
  const approvalIsMoreRecentThanLatestChanges = toSeconds(mostRecentNote.createdAt) > toSeconds(scanPart.updatedAt ?? 0);
  return approvalIsForCurrentScan && approvalIsMoreRecentThanLatestChanges;
};

/**
 * if the note-types 'editRequest', 'editResolution', or 'approval' are present,
 * this is true if the last one of those types was 'editResolution'
 * @param scanPart
 * @param approvalContext
 * @returns
 */
export const isScanPartEditResolved = (scanPart: ScanPartOptimized, approvalContext: ApprovalContext): boolean => {
  const scanNotes = approvalContext.scanNotesByScanPartId[scanPart.id] || [];
  const mostRecentNote = getMostRecentRelevantNote(scanNotes, scanPart.id, ['editRequest', 'editResolution', 'approval']);
  return mostRecentNote?.scanNoteType === 'editResolution';
};

/**
 * Get all unreviewed tasks in a group
 * @param group - The scan group to check
 * @param scanId - The current scan ID
 * @param scanNotesByScanPartId - Notes indexed by scan part ID
 * @param baselineScanRoot - Optional baseline scan for change order comparison
 * @param currentScanRoot - Current scan root for contractor defaults
 * @returns Array of unreviewed tasks
 */
export const getUnreviewedTasksInGroup = (
  group: ScanGroupOptimized,
  scanId: string,
  scanNotesByScanPartId: Record<string, ApiScanNote[]>,
  baselineScanRoot: ScanRootOptimized | undefined,
  currentScanRoot: ScanRootOptimized | undefined,
): ScanTaskOptimized[] => {
  const scopedTasks = group.tasks?.filter(isTaskShownOnScopeReport) ?? [];

  const approvalContext: ApprovalContext = {
    scanId,
    scanNotesByScanPartId,
    baselineTasksById: baselineScanRoot ? getAllTasksById(baselineScanRoot) : undefined,
    defaultContractorsByTrade: currentScanRoot?.defaultContractorsByTrade,
    baselineDefaultContractorsByTrade: baselineScanRoot?.defaultContractorsByTrade,
  };

  return scopedTasks.filter(task => !isScanPartReviewed(task, approvalContext));
};

export const getAllScopedLineItemsFromTask = (task: ScanTaskOptimized): ScanLineItem[] =>
  task.notApplicable
    ? []
    : task.options?.flatMap(option => option.notApplicable ? [] : option.lineItems?.filter(lineItem => lineItem.notApplicable ? [] : lineItem))
      ?? [];

export const getAllScopedLineItemsFromGroup = (group: ScanGroupOptimized): ScanLineItem[] =>
  group.tasks?.flatMap(task => task.notApplicable ? [] : getAllScopedLineItemsFromTask(task)) ?? [];

/**
 * An SOW line item is a line item where none of its parents are marked "not applicable," including itself.
 * @param scan
 */
export const getAllScopedLineItems = (scan: ScanRootOptimized): ScanLineItem[] =>
  scan.groups?.flatMap(group => group.notApplicable ? [] : getAllScopedLineItemsFromGroup(group)) ?? [];

type ScopeApprovalStatus = {
  approvedTaskCount: number;
  editRequestedTaskCount: number;
  editResolvedTaskCount: number;
  remainingTasksToReviewCount: number;
  canApprove: boolean;
  canRequestChanges: boolean;
  totalTasksToReviewCount: number;
  fieldAgentCanSubmitRequestChanges: boolean;
  firstApprovedTaskId: string | undefined;
  firstEditRequestedTaskId: string | undefined;
  firstEditResolvedTaskId: string | undefined;
  firstRemainingTaskId: string | undefined;
};

export const getScopeApprovalStatus = (
  scanRoot: ScanRootOptimized,
  scanId: string,
  scanNotesByScanPartId: Record<string, ApiScanNote[]>,
  baselineScanRoot?: ScanRootOptimized,
): ScopeApprovalStatus => {
  const scopedTasks = getAllScopedTasks(scanRoot);
  const totalTasksToReviewCount = scopedTasks.length;
  let remainingTasksToReviewCount = 0;
  let approvedTaskCount = 0;
  let editRequestedTaskCount = 0;
  let editResolvedTaskCount = 0;
  let firstRemainingTaskId: string | undefined = undefined;
  let firstApprovedTaskId: string | undefined = undefined;
  let firstEditRequestedTaskId: string | undefined = undefined;
  let firstEditResolvedTaskId: string | undefined = undefined;

  // Build approval context once (used by approval functions for all tasks)
  const approvalContext: ApprovalContext = {
    scanId,
    scanNotesByScanPartId,
    baselineTasksById: baselineScanRoot ? getAllTasksById(baselineScanRoot) : undefined,
    defaultContractorsByTrade: scanRoot.defaultContractorsByTrade,
    baselineDefaultContractorsByTrade: baselineScanRoot?.defaultContractorsByTrade,
  };

  for (const task of scopedTasks) {
    // Check approval status (which will automatically handle unchanged Change Order tasks)
    const approvalStatus = getScanPartApprovalStatus(task, approvalContext);

    if (approvalStatus === 'approved') {
      approvedTaskCount++;
      if (!firstApprovedTaskId) {
        firstApprovedTaskId = task.id;
      }
    } else if (approvalStatus === 'editRequested') {
      editRequestedTaskCount++;
      if (!firstEditRequestedTaskId) {
        firstEditRequestedTaskId = task.id;
      }
    } else if (approvalStatus === 'editResolved') {
      editRequestedTaskCount++;
      editResolvedTaskCount++;
      if (!firstEditResolvedTaskId) {
        firstEditResolvedTaskId = task.id;
      }
    } else {
      remainingTasksToReviewCount++;
      if (!firstRemainingTaskId) {
        firstRemainingTaskId = task.id;
      }
    }
  }

  return {
    approvedTaskCount,
    editRequestedTaskCount,
    editResolvedTaskCount,
    remainingTasksToReviewCount,
    firstApprovedTaskId,
    firstEditRequestedTaskId,
    firstEditResolvedTaskId,
    canApprove: approvedTaskCount === totalTasksToReviewCount, // 100% approved
    canRequestChanges: (editRequestedTaskCount > editResolvedTaskCount) && remainingTasksToReviewCount === 0, // all tasks done, at least one edit requested
    totalTasksToReviewCount,
    firstRemainingTaskId,
    fieldAgentCanSubmitRequestChanges: editRequestedTaskCount === editResolvedTaskCount, // 100% of edit requests have been resolved
  };
};
